import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreVert from "@mui/icons-material/MoreVert";
import { handleCloneAssessment, handleUpdateAssessment } from "../services";
import classNames from "classnames";
import { STATIC_STRINGS } from "@asez/shared";
import { useNavigate } from "react-router-dom";
import { forwardRef, useState } from "react";
import { TableVirtuoso, TableVirtuosoProps } from "react-virtuoso";

const RenderTable = ({
  searchTerm,
  data,
  setFetchOptions,
  updateAssessmentId,
}: any) => {
  const navigate = useNavigate();
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filteredAssessments = data?.assessments?.filter((assessment) => {
    if (!assessment?.name) return false;

    const lowerCaseName = assessment.name.toLowerCase().trim();
    const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);

    return searchWords.every((word) => lowerCaseName.includes(word));
  });

  const handleMenuClick = (event: React.MouseEvent, id: string) => {
    const rect = (
      event.currentTarget as HTMLButtonElement
    ).getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom,
      left: rect.right,
    });
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setSelectedId(null);
  };

  const handleAssessmentClick = (assessment: any) => {
    if (assessment.status == STATIC_STRINGS.STATUS.DRAFT) {
      navigate(`/edit/${assessment.id}`);
    } else {
      navigate(`/report/${assessment.id}`);
    }
  };

  const getStatusClass = (status: string) => {
    const staticStatus = STATIC_STRINGS.STATUS;

    switch (status) {
      case staticStatus.ACTIVE:
        return "active";

      case staticStatus.DRAFT:
        return "draft";

      case staticStatus.PAUSED:
        return "paused";

      default:
        return "";
    }
  };

  const TableComponents: TableVirtuosoProps<any, unknown>["components"] = {
    Scroller: forwardRef((props, ref) => (
      <TableContainer {...props} ref={ref} />
    )),
    Table: (props) => <Table {...props} stickyHeader />,
    TableHead: forwardRef((props, ref) => (
      <TableHead
        {...props}
        ref={ref}
        sx={{
          backgroundColor: "var(--color-table-header)",
          "& .MuiTableCell-head": {
            backgroundColor: "var(--color-table-header)",
          },
        }}
      />
    )),
    TableRow: ({ item: assessment, ...props }) => (
      <TableRow
        {...props}
        hover
        onClick={() => handleAssessmentClick(assessment)}
      />
    ),
    TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
  };

  return (
    <TableVirtuoso
      style={{ maxHeight: "calc(100% - 56px)" }}
      data={filteredAssessments}
      components={TableComponents}
      fixedHeaderContent={() => (
        <TableRow>
          <TableCell sx={{ fontWeight: 600 }}>
            {STATIC_STRINGS.TABLE_HEADERS.ASSESSMENT_NAME}
          </TableCell>
          <TableCell sx={{ fontWeight: 600 }}>
            {STATIC_STRINGS.TABLE_HEADERS.JOB_ROLE}
          </TableCell>
          <TableCell sx={{ fontWeight: 600 }}>
            {STATIC_STRINGS.TABLE_HEADERS.EXPERIENCE}
          </TableCell>
          <TableCell sx={{ fontWeight: 600 }}>
            {STATIC_STRINGS.TABLE_HEADERS.CANDIDATES}
          </TableCell>
          <TableCell sx={{ width: "125px", fontWeight: 600 }}>
            {STATIC_STRINGS.TABLE_HEADERS.DEADLINE}
          </TableCell>
          <TableCell sx={{ fontWeight: 600 }}>
            <div className="flex justify-center w-full">
              {STATIC_STRINGS.TABLE_HEADERS.STATUS}
            </div>
          </TableCell>
          <TableCell sx={{ width: "125px", fontWeight: 600 }}>
            {STATIC_STRINGS.TABLE_HEADERS.DATE_CREATED}
          </TableCell>
          <TableCell sx={{ fontWeight: 600 }}>
            <div className="flex justify-center w-full">
              {STATIC_STRINGS.TABLE_HEADERS.ACTIONS}
            </div>
          </TableCell>
        </TableRow>
      )}
      itemContent={(idx, assessment) => (
        <>
          <TableCell
            sx={{
              borderBottom: idx == filteredAssessments.length - 1 ? "none" : "",
            }}
          >
            {assessment.name}
          </TableCell>
          <TableCell
            sx={{
              borderBottom: idx == filteredAssessments.length - 1 ? "none" : "",
            }}
          >
            {assessment.jobRole || STATIC_STRINGS.N_A}
          </TableCell>
          <TableCell
            sx={{
              borderBottom: idx == filteredAssessments.length - 1 ? "none" : "",
            }}
          >
            {assessment.yearsOfExperience.min} -{" "}
            {assessment.yearsOfExperience.max} yrs
          </TableCell>
          <TableCell
            sx={{
              borderBottom: idx == filteredAssessments.length - 1 ? "none" : "",
            }}
          >
            {assessment.usercount}
          </TableCell>
          <TableCell
            sx={{
              borderBottom: idx == filteredAssessments.length - 1 ? "none" : "",
            }}
          >
            {assessment.assessmentLinkActiveDuration?.endTime
              ? new Date(
                  assessment.assessmentLinkActiveDuration.endTime
                ).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : STATIC_STRINGS.N_A}
          </TableCell>
          <TableCell
            sx={{
              borderBottom: idx == filteredAssessments.length - 1 ? "none" : "",
              textAlign: "center",
            }}
          >
            {assessment?.status && (
              <span className={classNames(getStatusClass(assessment.status))}>
                {assessment.status}
              </span>
            )}
          </TableCell>
          <TableCell
            sx={{
              borderBottom: idx == filteredAssessments.length - 1 ? "none" : "",
            }}
          >
            {new Date(assessment.createdAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </TableCell>
          <TableCell
            sx={{
              borderBottom: idx == filteredAssessments.length - 1 ? "none" : "",
            }}
          >
            <div className="flex items-center justify-end w-full">
              <button
                disabled={assessment.status === STATIC_STRINGS.STATUS.DRAFT}
                className={classNames(
                  "mr-2 btn btn-outline",
                  assessment.status === STATIC_STRINGS.STATUS.DRAFT &&
                    "btn-outline-disabled"
                )}
              >
                {STATIC_STRINGS.VIEW}
              </button>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClick(e, assessment.id);
                }}
              >
                <MoreVert />
              </IconButton>
              {selectedId === assessment.id && (
                <Menu
                  open={!!menuPosition}
                  anchorReference="anchorPosition"
                  anchorPosition={menuPosition ?? { top: 0, left: 0 }}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  onClose={(e: Event) => {
                    e.stopPropagation();
                    handleMenuClose();
                  }}
                >
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/edit/${selectedId}`);
                    }}
                  >
                    {STATIC_STRINGS.MENU_ITEMS.EDIT}
                  </MenuItem>
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      updateAssessmentId(assessment.id);
                    }}
                  >
                    {STATIC_STRINGS.MENU_ITEMS.DELETE}
                  </MenuItem>
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloneAssessment(assessment.id);
                      setFetchOptions({
                        requestId: new Date().getTime().toString(),
                      });
                    }}
                  >
                    {STATIC_STRINGS.MENU_ITEMS.CLONE}
                  </MenuItem>
                  {assessment.status === STATIC_STRINGS.STATUS.ACTIVE && (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateAssessment(
                          assessment.id,
                          STATIC_STRINGS.STATUS.PAUSED
                        );
                        setFetchOptions({
                          requestId: new Date().getTime().toString(),
                        });
                      }}
                    >
                      {STATIC_STRINGS.MENU_ITEMS.PAUSED}
                    </MenuItem>
                  )}
                  {assessment.status === STATIC_STRINGS.STATUS.PAUSED && (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateAssessment(
                          assessment.id,
                          STATIC_STRINGS.STATUS.ACTIVE
                        );
                        setFetchOptions({
                          requestId: new Date().getTime().toString(),
                        });
                      }}
                    >
                      {STATIC_STRINGS.MENU_ITEMS.MAKE_ACTIVE}
                    </MenuItem>
                  )}
                </Menu>
              )}
            </div>
          </TableCell>
        </>
      )}
    />
  );
};

export default RenderTable;
